// Block selection rules a view applies to top-level blocks: the node selection of a block range and the vertical line
// moves onto and out of a selectable block decorator. Donors: `@lexical/rich-text` 0.50 `KEY_ARROW_UP_COMMAND` /
// `KEY_ARROW_DOWN_COMMAND` (`$tryDecoratorLineNavigation`, `$exitNodeSelectionToward`) and the Lexical view's
// BlockSelectionPlugin (`$setBlockRange`), which the Lexical view runs itself.
import type { Block } from '@w/content-blocks/src/block'
import type { CorePoint, CoreSelection, CoreState } from './state'
import { blockGap, children, gapOf, gapPoint, isHeader, isHost, isRow, locate, plain, pointAt, rows } from './tree-read'

/**
 * How structural edits treat a block-level block, whatever its type:
 * - `row`: a text block (paragraph, heading, code, table cell); inline wrappers stay inside it.
 * - `container`: a wrapper of blocks (quote, callout…); boundary keys lift its rows out (ProseMirror `lift`).
 * - `list`: a list or list item; boundary keys follow the list rules (indent, outdent, item split).
 * - `grid`: fixed slots (table, table row, columns, column; Lexical shadow roots); edits never dissolve them.
 * - `header`: the page title and metadata, edited in their own fields and never removed by an edit.
 * - `atom`: a block edited whole (image, formula, divider, media, page card, question…; Lexical block decorator).
 * - `inline`: text-level content of a row (text, marks, links, inline formula…); never a structural target.
 */
export type BlockClass = 'row' | 'container' | 'list' | 'grid' | 'header' | 'atom' | 'inline'

// `InlineBlock` in the schema, plus the definitions an inline slot references (`code` is a row: its text is a line).
const INLINE = new Set<Block['type']>(['text', 'chars', 'br', 'a', 'em', 'l', 'mention', 'cloze', 'comment', 'criticComment', 'diff', 'review', 'definition'])
const LIST = new Set<Block['type']>(['list', 'li'])
const GRID = new Set<Block['type']>(['table', 'tr', 'cols', 'col'])

export function blockClass(block: Block): BlockClass {
  if (isRow(block)) return 'row'
  if (INLINE.has(block.type)) return 'inline'
  if (GRID.has(block.type)) return 'grid'
  if (LIST.has(block.type)) return 'list'
  if (isHeader(block)) return 'header'
  return isHost(block) ? 'container' : 'atom'
}

/** The fixed grid slot a text row sits in: its table cell, else its innermost column; none outside grids. Character
 * deletion never crosses a slot's edge (Lexical `isShadowRoot` on table cells and layout items). */
export function gridSlot(row: { block: Block; parents: readonly Block[] }): Block | undefined {
  if (row.block.type === 'td') return row.block
  return row.parents.findLast(parent => parent.type === 'col' || parent.type === 'td')
}

/** A block a node selection selects whole: an atom (Lexical `$isSelectableBlockDecorator`). */
export function isSelectableBlock(block: Block): boolean {
  return blockClass(block) === 'atom'
}

/**
 * The node selection of the top-level blocks from `anchor` to `focus` (block ids), in either direction; null for an
 * unknown id. A view keying its blocks otherwise (Lexical node keys) passes those keys as the ids.
 */
export function blockRange(blocks: readonly { id: string }[], anchor: string, focus: string): CoreSelection | null {
  const from = blocks.findIndex((block) => block.id === anchor)
  const to = blocks.findIndex((block) => block.id === focus)
  if (from < 0 || to < 0) return null
  const nodes = blocks.slice(Math.min(from, to), Math.max(from, to) + 1).map((block) => block.id)
  return { anchor: { blockId: anchor, offset: 0 }, focus: { blockId: focus, offset: 0 }, nodes }
}

const caret = (point: CorePoint): CoreSelection => ({ anchor: point, focus: point })

/**
 * The selection a vertical line move (ArrowUp/ArrowDown without Shift) takes onto the selectable block beside the
 * caret's top-level block, or null when the move is the view's own: from a top-level gap onto the block in that
 * direction; from a text block when that block is empty or the view's line move leaves it (`leavesBlock`, the view's
 * geometry answer to Lexical's `domSelection.modify('move', …, 'line')` probe). A range, a node or table selection and a
 * caret inside a table cell move as usual.
 */
export function decoratorLineMove(state: Pick<CoreState, 'blocks' | 'selection'>, direction: 'up' | 'down', leavesBlock: boolean): CoreSelection | null {
  const { blocks, selection } = state
  if (!selection || selection.nodes?.length || selection.table) return null
  const step = direction === 'up' ? -1 : 1
  const node = (block: Block | undefined) => (block && isSelectableBlock(block) ? blockRange(blocks, block.id, block.id) : null)
  const gap = gapOf(blocks, selection.focus)
  if (gap) {
    const same = gapOf(blocks, selection.anchor)
    if (gap.siblings !== blocks || same?.siblings !== gap.siblings || same.index !== gap.index) return null
    return node(blocks[direction === 'up' ? gap.index - 1 : gap.index])
  }
  const entries = rows(blocks)
  const [anchor, focus] = [locate(entries, selection.anchor), locate(entries, selection.focus)]
  if (anchor.row !== focus.row || anchor.offset !== focus.offset) return null
  const row = entries[focus.row]
  if (!row || row.block.type === 'td') return null
  const top = row.parents[0] ?? row.block
  const sibling = blocks[blocks.indexOf(top) + step]
  if (!sibling || !isSelectableBlock(sibling)) return null
  // imp11: moving down past a divider reaches the following text at the same text column.
  if (direction === 'down' && sibling.type === 'hr' && leavesBlock) {
    const next = blocks[blocks.indexOf(sibling) + 1]
    const target = next && rows([next])[0]
    if (target) return caret(pointAt(target, Math.min(focus.offset, plain(target.block).length)))
  }
  return plain(top).length === 0 || leavesBlock ? node(sibling) : null
}

/**
 * The caret a vertical move (ArrowUp/ArrowDown without Shift) takes out of a node selection, from its first block: up
 * to the end of the text block before it, down to the start of the text block after it; beside another selectable
 * block or the document edge, the top-level gap on that side. Null without a node selection.
 */
export function exitNodeSelection(state: Pick<CoreState, 'blocks' | 'selection'>, direction: 'up' | 'down'): CoreSelection | null {
  const { blocks, selection } = state
  // Wherever the node sits: a block nested in a container exits into its own block list.
  const gap = selection?.nodes?.[0] === undefined ? undefined : blockGap(blocks, selection.nodes[0])
  if (!gap) return null
  const { siblings, parent, index } = gap
  const sibling = siblings[index + (direction === 'up' ? -1 : 1)]
  const edges = sibling && !isSelectableBlock(sibling) ? rows([sibling]) : []
  const edge = direction === 'up' ? edges.at(-1) : edges[0]
  if (edge) return caret(pointAt(edge, direction === 'up' ? plain(edge.block).length : 0))
  return caret(gapPoint({ siblings, parent, index: direction === 'up' ? index : index + 1 }))
}

/** The node selection of one block, wherever it sits (Lexical `NodeSelection.add`). */
export function nodeSelection(id: string): CoreSelection {
  const point = { blockId: id, offset: 0 }
  return { anchor: point, focus: point, nodes: [id] }
}

/** The blocks a node selection names, at any depth, in document order; a selected block's own descendants are part
 * of it (Lexical `NodeSelection.getNodes` by key). */
export function selectedBlocks(blocks: Block[], ids: readonly string[]): Block[] {
  return blocks.flatMap(block => ids.includes(block.id) ? [block]
    : 'children' in block && Array.isArray(block.children) ? selectedBlocks(block.children, ids) : [])
}

/**
 * The blocks a handle drag of top-level block `id` moves (BlockNote SideMenu dragging.ts `dragStart`: a block inside a
 * multi-block selection drags every selected block): the node selection when it holds `id`; the top-level blocks a
 * text range touches when one of them is `id`; else `id` alone. Document order.
 */
export function draggedBlocks(state: Pick<CoreState, 'blocks' | 'selection'>, id: string): string[] {
  const selection = state.selection
  if (selection?.nodes?.includes(id)) return selection.nodes
  if (!selection || selection.nodes?.length || selection.table || gapOf(state.blocks, selection.anchor) ||
    gapOf(state.blocks, selection.focus)) return [id]
  const entries = rows(state.blocks)
  const [start, end] = [locate(entries, selection.anchor).row, locate(entries, selection.focus).row].toSorted((a, b) => a - b)
  const touched = [...new Set(entries.slice(start, end + 1).map((entry) => (entry.parents[0] ?? entry.block).id))]
  return touched.length > 1 && touched.includes(id) ? touched : [id]
}

/** Block `id` and its ancestors, outermost first; empty for an unknown id. */
function pathTo(blocks: readonly Block[], id: string): Block[] {
  for (const block of blocks) {
    if (block.id === id) return [block]
    const inner = pathTo(children(block), id)
    if (inner.length > 0) return [block, ...inner]
  }
  return []
}

/**
 * The block Notion counts as the one holding block `id` (writing-and-editing-basics, keyboard-shortcuts): its innermost
 * list item, with its nested items, else its top-level block. Esc, a modifier click and Shift+click all select it.
 */
export function ownBlock(blocks: readonly Block[], id: string): Block | undefined {
  const path = pathTo(blocks, id)
  return path.findLast(block => block.type === 'li') ?? path[0]
}

/** The node selection of block `id` among its siblings (a list item among its list's items). */
function ownRange(blocks: readonly Block[], id: string, focus = id): CoreSelection | null {
  return blockRange(blockGap(blocks as Block[], id)?.siblings ?? blocks, id, focus)
}

/**
 * Notion Esc: with a caret or text range, the node selection of the block the focus is in (`ownBlock`); with selected
 * blocks, no selection (Esc again clears them).
 */
export function escapeSelection(state: Pick<CoreState, 'blocks' | 'selection'>): CoreSelection | null {
  const { blocks, selection } = state
  if (!selection || selection.nodes?.length) return null
  const own = ownBlock(blocks, selection.focus.blockId)
  return own ? ownRange(blocks, own.id) : null
}

/**
 * Notion arrow keys on selected blocks: Shift+ArrowUp/Down first collapses discontiguous blocks to the focus;
 * otherwise it moves the focus one block among the focus block's siblings;
 * ArrowUp/Down selects the sibling beside the selection instead (a lone selected atom leaves for a caret,
 * `exitNodeSelection`). Null without selected blocks.
 */
export function arrowBlockSelection(
  state: Pick<CoreState, 'blocks' | 'selection'>, direction: 'up' | 'down', extend: boolean,
): CoreSelection | null {
  const { blocks, selection } = state
  const nodes = selection?.nodes
  if (!selection || !nodes?.length) return null
  const step = direction === 'up' ? -1 : 1
  if (extend) {
    const siblings = blockGap(blocks, selection.focus.blockId)?.siblings ?? blocks
    const range = blockRange(siblings, nodes[0], nodes.at(-1) ?? nodes[0])
    if (range?.nodes?.length !== nodes.length) return blockRange(siblings, selection.focus.blockId, selection.focus.blockId)
    const index = siblings.findIndex(item => item.id === selection.focus.blockId)
    const next = siblings[Math.max(0, Math.min(siblings.length - 1, index + step))]
    return next ? blockRange(siblings, selection.anchor.blockId, next.id) : null
  }
  const first = pathTo(blocks, nodes[0]).at(-1)
  if (nodes.length === 1 && first && isSelectableBlock(first)) {
    const gap = blockGap(blocks, first.id)
    if (gap && !gap.siblings[gap.index + step]) return selection
    return exitNodeSelection(state, direction)
  }
  const edge = blockGap(blocks, direction === 'up' ? nodes[0] : nodes.at(-1) ?? nodes[0])
  const next = edge?.siblings[edge.index + step] ?? edge?.siblings[edge.index]
  return next && edge ? blockRange(edge.siblings, next.id, next.id) : null
}

/**
 * Notion Mod+Shift+click (Alt+Shift+click elsewhere) on block `id` (an `ownBlock`): adds it to the selected blocks or
 * removes it, in document order; with no selected block it selects that block alone.
 */
export function toggleBlockSelection(state: Pick<CoreState, 'blocks' | 'selection'>, id: string): CoreSelection | null {
  const selected = state.selection?.nodes ?? []
  const nodes = selected.includes(id) ? selected.filter(item => item !== id) : [...selected, id]
  const order = (items: readonly Block[]): string[] => items.flatMap(item => [item.id, ...order(children(item))])
  const ordered = order(state.blocks).filter(item => nodes.includes(item))
  const edge = (item: string | undefined) => ({ blockId: item ?? '', offset: 0 })
  return ordered.length > 0 ? { anchor: edge(ordered[0]), focus: edge(selected.includes(id) ? ordered.at(-1) : id), nodes: ordered } : null
}

/**
 * Shift+click on block `id` (an `ownBlock`) with selected blocks: the range from the anchor among the anchor's siblings
 * (list items of one list); a block outside them extends over the top-level blocks holding both.
 */
export function extendBlockSelection(state: Pick<CoreState, 'blocks' | 'selection'>, id: string): CoreSelection | null {
  const { blocks, selection } = state
  if (!selection?.nodes?.length) return null
  const siblings = blockGap(blocks, selection.anchor.blockId)?.siblings ?? blocks
  if (siblings.some(item => item.id === id)) return blockRange(siblings, selection.anchor.blockId, id)
  const anchorTop = pathTo(blocks, selection.anchor.blockId)[0]
  const top = pathTo(blocks, id)[0]
  return anchorTop && top ? blockRange(blocks, anchorTop.id, top.id) : null
}
